import { useMemo } from 'react';
import type {
  ApprovalChainState,
  RoleEditorFormValues,
  RoleEditorSection,
  SectionCompletion,
} from './types';

interface Args {
  values: Partial<RoleEditorFormValues>;
  approvalChain: ApprovalChainState;
  isMobileUser: boolean;
  isEditing: boolean;
  activeSection: RoleEditorSection;
}

/**
 * Pure selector that derives sidebar dot states from form values.
 *
 * Rules:
 *   identity      — name ≥ 2 chars
 *   department    — non-empty (auto-`done` on edit, since dept is locked)
 *   access        — roleType present (hidden for mobile users → `na`)
 *   approval      — approver picked when role is requester/both, no cycle (hidden for mobile → `na`)
 *   permissions   — at least one (module, action) pair selected
 *   menu          — always `optional`; never blocks progress
 *
 * The active section is always `'current'` regardless of its filled state
 * so the user sees a pulsing indicator on where they are.
 */
export function useSectionCompletion({
  values,
  approvalChain,
  isMobileUser,
  isEditing,
  activeSection,
}: Args): Record<RoleEditorSection, SectionCompletion> {
  return useMemo(() => {
    const out: Record<RoleEditorSection, SectionCompletion> = {
      identity: 'todo',
      department: 'todo',
      access: 'todo',
      approval: 'todo',
      permissions: 'todo',
      menu: 'optional',
    };

    out.identity = (values.name ?? '').trim().length >= 2 ? 'done' : 'todo';

    if (isEditing) {
      out.department = 'done';
    } else {
      out.department = values.department ? 'done' : 'todo';
    }

    if (isMobileUser) {
      out.access = 'na';
      out.approval = 'na';
    } else {
      out.access = values.roleType ? 'done' : 'todo';

      const needsApprover =
        values.roleType === 'requester' || values.roleType === 'both';
      if (approvalChain.cycle) {
        out.approval = 'warn';
      } else if (needsApprover && !values.approverRoleId) {
        out.approval = 'warn';
      } else if (!needsApprover) {
        out.approval = 'done';
      } else {
        out.approval = approvalChain.steps.length > 0 ? 'done' : 'todo';
      }
    }

    const permsCount = Object.values(values.permissions ?? {}).reduce(
      (acc, arr) => acc + (Array.isArray(arr) ? arr.length : 0),
      0,
    );
    out.permissions = permsCount > 0 ? 'done' : 'todo';

    if (out[activeSection] !== 'na') {
      out[activeSection] = 'current';
    }

    return out;
  }, [values, approvalChain, isMobileUser, isEditing, activeSection]);
}
